import React, { useContext, useMemo } from 'react';

import { ActiveOrgContext } from './ActiveOrgContext';


/** Derive the capability flags for a role. Mirrors the behaviour already coded
 * across the pages (#204):
 *  - canWrite: Owner / Manager / Lister (everyone but Viewer).
 *  - canSeeFinancials: everyone EXCEPT Lister — Viewer keeps financial sight
 *    (Garage `financialBlind` and Sales gating are Lister-only today).
 *  - canManageTeam / canManageOrg: Owner / Manager.
 *  - canBilling / canDanger: Owner only.
 * `null` role (no active membership) yields all-false. */
function derive(role) {
  const isOwner = role === 'owner';
  const isManager = role === 'manager';
  const isLister = role === 'lister';
  const isViewer = role === 'viewer';

  return {
    role: role || null,
    isOwner,
    isManager,
    isLister,
    isViewer,
    canWrite: isOwner || isManager || isLister,
    canSeeFinancials: Boolean(role) && !isLister,
    canManageTeam: isOwner || isManager,
    canManageOrg: isOwner || isManager,
    canBilling: isOwner,
    canDanger: isOwner,
  };
}


// Default = the all-false "no membership" capability set, so a consumer
// rendered outside the provider (error boundaries, stories) degrades to no
// permissions instead of crashing on `const { canWrite } = usePermissions()`.
const PermissionsContext = React.createContext(derive(null));


/** Capability context derived from the ACTIVE membership (#204). Sits inside
 * ActiveOrgProvider so it always reflects the org the user is currently in. */
function PermissionsProvider({ children }) {
  const activeOrg = useContext(ActiveOrgContext);
  const role = activeOrg?.membership?.role || null;

  const contextValue = useMemo(() => derive(role), [role]);

  return <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>;
}

PermissionsProvider.displayName = 'PermissionsProvider';
export { PermissionsContext, PermissionsProvider };
